import { userRoles } from "@/lib/common";

export const ContentsquareEvent = {
  SESSION_STARTED: "session_started",
  USER_LOGGED_OUT: "user_logged_out",
  COMPANY_SELECTED: "company_selected",
  HOME_VIEWED: "home_viewed",
  HOME_ACCOUNTS_TAB_VIEWED: "home_accounts_tab_viewed",
  HOME_ANALYTICS_TAB_VIEWED: "home_analytics_tab_viewed",
  DASHBOARD_VIEWED: "dashboard_viewed",
  INSIGHT_HUB_VIEWED: "insight_hub_viewed",
  CUSTOM_DASHBOARD_VIEWED: "custom_dashboard_viewed",
  PORTFOLIO_VIEWED: "portfolio_viewed",
  MESSAGES_VIEWED: "messages_viewed",
  RECOVERY_VIEWED: "recovery_viewed",
  TECH_PERFORMANCE_VIEWED: "tech_performance_viewed",
  CALL_INTEL_VIEWED: "call_intel_viewed",
  CUSTOMER_PROFILE_VIEWED: "customer_profile_viewed",
  ONBOARDING_SETUP_VIEWED: "onboarding_setup_viewed",
  COACH_VIEWED: "coach_viewed",
  GAME_VIEWED: "game_viewed",
  SETTINGS_VIEWED: "settings_viewed",
  SETTINGS_ACCOUNT_VIEWED: "settings_account_viewed",
  SETTINGS_MANAGE_USERS_VIEWED: "settings_manage_users_viewed",
  SETTINGS_BILLING_VIEWED: "settings_billing_viewed",
  SETTINGS_ESTIMATE_AVERAGES_VIEWED: "settings_estimate_averages_viewed",
  SETTINGS_RECOVERY_STEPS_VIEWED: "settings_recovery_steps_viewed",
  SETTINGS_RR_SETTINGS_VIEWED: "settings_rr_settings_viewed",
  SETTINGS_FSM_SETTINGS_VIEWED: "settings_fsm_settings_viewed",
  SETTINGS_NOTIFICATIONS_VIEWED: "settings_notifications_viewed",
  SETTINGS_WEEKLY_REPORTS_VIEWED: "settings_weekly_reports_viewed",
  SETTINGS_CONFIGURATION_VIEWED: "settings_configuration_viewed",
  SETTINGS_MISC_VIEWED: "settings_misc_viewed",
} as const;

export type ContentsquareEventName =
  | (typeof ContentsquareEvent)[keyof typeof ContentsquareEvent]
  // Tech BoxScore onboarding events, built from typed parts in features/tech-onboarding/tracking.ts.
  | `tech_onboarding_${string}`
  | `tech_guide_${string}`;

/** Settings sidebar tab id to Contentsquare event. Keep in sync with SettingV2's tab list. */
export const SETTINGS_TAB_EVENTS: Record<string, ContentsquareEventName> = {
  account: ContentsquareEvent.SETTINGS_ACCOUNT_VIEWED,
  "manage-users": ContentsquareEvent.SETTINGS_MANAGE_USERS_VIEWED,
  billing: ContentsquareEvent.SETTINGS_BILLING_VIEWED,
  "historical-averages": ContentsquareEvent.SETTINGS_ESTIMATE_AVERAGES_VIEWED,
  workflow: ContentsquareEvent.SETTINGS_RECOVERY_STEPS_VIEWED,
  "rr-settings": ContentsquareEvent.SETTINGS_RR_SETTINGS_VIEWED,
  "fsm-settings": ContentsquareEvent.SETTINGS_FSM_SETTINGS_VIEWED,
  notifications: ContentsquareEvent.SETTINGS_NOTIFICATIONS_VIEWED,
  "weekly-reports": ContentsquareEvent.SETTINGS_WEEKLY_REPORTS_VIEWED,
  configuration: ContentsquareEvent.SETTINGS_CONFIGURATION_VIEWED,
  misc: ContentsquareEvent.SETTINGS_MISC_VIEWED,
};

/**
 * Never recorded: credentials on screen (sign in, password reset, self-serve onboarding), a
 * checkout token in the address (/tech-boxscore/pay, which goes straight on to Stripe), and the
 * public TV board (share link + code, no login).
 */
const NO_RECORD_PATHS = [
  "/login",
  "/login-m",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/self-onboarding",
  "/tech-boxscore/pay",
  "/tv",
];

/**
 * The Tech BoxScore onboarding (the wizard and its setup guide). Reached before sign in, so the app's
 * user lookup must not run there: /api/user/me would 401 and axios would bounce the visitor to /login.
 * The pages load the tag themselves, through trackPage, once the server says the visitor may be
 * recorded (Global Privacy Control, ad opt-out). See docs/TECH_ONBOARDING_CONTENTSQUARE_TRACKING.md.
 */
const ONBOARDING_PATHS = ["/tech-boxscore"];

function matchesPath(pathname: string | null, paths: readonly string[]): boolean {
  if (!pathname) return false;
  return paths.some(p => pathname === p || pathname.startsWith(p + "/"));
}

/** The tag never loads here. */
export function isNoRecordPath(pathname: string | null): boolean {
  return matchesPath(pathname, NO_RECORD_PATHS);
}

/** Onboarding pages: they send their own page views, and only once the visitor may be recorded. */
export function isOnboardingPath(pathname: string | null): boolean {
  return !isNoRecordPath(pathname) && matchesPath(pathname, ONBOARDING_PATHS);
}

/** Pages before sign in: no app user lookup, no identify from the provider. */
export function isAuthPath(pathname: string | null): boolean {
  return isNoRecordPath(pathname) || matchesPath(pathname, ONBOARDING_PATHS);
}

/** Map authenticated app paths to a page-view event (null = no page event). */
export function getPageViewEventFromPathname(
  pathname: string | null,
): ContentsquareEventName | null {
  if (!pathname) return null;
  if (pathname === "/home" || pathname.startsWith("/home/")) {
    return ContentsquareEvent.HOME_VIEWED;
  }

  // /{companyId}/{section}/...
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const section = parts[1];

  switch (section) {
    case "dashboard":
    case "dashboard2":
      return ContentsquareEvent.DASHBOARD_VIEWED;
    case "insight":
      return ContentsquareEvent.INSIGHT_HUB_VIEWED;
    case "custom-dashboard":
      return ContentsquareEvent.CUSTOM_DASHBOARD_VIEWED;
    case "source":
      return ContentsquareEvent.PORTFOLIO_VIEWED;
    case "chat":
      return ContentsquareEvent.MESSAGES_VIEWED;
    case "recovery":
      return ContentsquareEvent.RECOVERY_VIEWED;
    case "tech-boxscore":
    case "tech-performance":
      return ContentsquareEvent.TECH_PERFORMANCE_VIEWED;
    case "table":
      return ContentsquareEvent.CALL_INTEL_VIEWED;
    case "customer":
      return ContentsquareEvent.CUSTOMER_PROFILE_VIEWED;
    case "setup":
      return ContentsquareEvent.ONBOARDING_SETUP_VIEWED;
    case "coach":
      return ContentsquareEvent.COACH_VIEWED;
    case "game":
      return ContentsquareEvent.GAME_VIEWED;
    case "settings":
      return ContentsquareEvent.SETTINGS_VIEWED;
    default:
      return null;
  }
}

export type ContentsquareRoleLabel = "Super Admin" | "Client Admin" | "CSR";

export type ContentsquareIdentifyAttributes = {
  role: ContentsquareRoleLabel | string;
  userName?: string;
  email?: string;
  companyId?: string;
  companyName?: string;
};

/** Contentsquare Tag ID - single source of truth for the tag and this module. */
export const CONTENTSQUARE_TAG_ID = process.env.NEXT_PUBLIC_CONTENTSQUARE_ID ?? "";

/** Set NEXT_PUBLIC_CONTENTSQUARE_DEBUG=true to allow tracking on local `npm run dev`. */
const DEBUG_ENABLED =
  (process.env.NEXT_PUBLIC_CONTENTSQUARE_DEBUG ??
    // Deprecated alias, kept so existing .env files and Vercel settings keep working.
    process.env.NEXT_PUBLIC_HOTJAR_DEBUG) === "true";

const DEDUPE_STORAGE_PREFIX = "cs_event:";

/**
 * Vercel builds preview/staging deployments with NODE_ENV=production, so gating on
 * NODE_ENV alone records staging and QA traffic into the production tag. Gate on the
 * deployment environment instead - NEXT_PUBLIC_VERCEL_ENV is "production" only on the
 * production deployment. NEXT_PUBLIC_CONTENTSQUARE_ENV overrides it for non-Vercel hosts.
 */
function isProductionDeployment(): boolean {
  const deployEnv =
    process.env.NEXT_PUBLIC_CONTENTSQUARE_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV;
  return deployEnv === "production";
}

export function isTrackingEnabled(): boolean {
  if (!CONTENTSQUARE_TAG_ID) return false;
  return isProductionDeployment() || DEBUG_ENABLED;
}

/**
 * Query params that change which page the user is on. Everything else (date ranges,
 * filters, drill-downs) is left out so one page does not fragment into thousands of URLs.
 */
const TRACKED_QUERY_PARAMS = ["tab", "step"] as const;

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PHONE_RE = /^\+?\d[\d\-.()\s]{5,}$/;

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Collapse dynamic route segments into placeholders (/:companyId/dashboard) so every
 * client shares one page URL in Contentsquare. Without this each company gets its own
 * page, which splits heatmaps and zoning across hundreds of near-identical URLs - and
 * customer phone numbers end up in page URLs.
 */
export function normalizeTrackingPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";

  const normalized = segments.map((segment, index) => {
    const value = decodeSegment(segment);
    if (index === 0 && OBJECT_ID_RE.test(value)) return ":companyId";
    if (OBJECT_ID_RE.test(value) || UUID_RE.test(value)) return ":id";
    if (PHONE_RE.test(value)) return ":phone";
    return segment;
  });

  return "/" + normalized.join("/");
}

/** Page URL reported to Contentsquare: normalized path plus the params that define the page. */
export function buildTrackingPath(pathname: string | null, search: string): string {
  const path = normalizeTrackingPath(pathname ?? "/");
  const params = new URLSearchParams(search);
  const tracked = new URLSearchParams();

  for (const key of TRACKED_QUERY_PARAMS) {
    const value = params.get(key);
    if (value) tracked.set(key, value);
  }

  const query = tracked.toString();
  return query ? `${path}?${query}` : path;
}

const CONTENTSQUARE_SRC = `https://t.contentsquare.net/uxa/${CONTENTSQUARE_TAG_ID}.js`;

/** Survives React Strict Mode remounts so the tag is only bootstrapped once. */
let hasBootstrappedTag = false;
let lastTrackedPath: string | null = null;
/** Set when the tag script fails to load (blocked, offline) so we stop queueing forever. */
let tagLoadFailed = false;

/**
 * Report a page view, loading the tag on the first one. Official Contentsquare main tag behavior
 * for SPAs: the first page is `setPath` + inject the script, later ones are `trackPageview`.
 *
 * `trackingPath` is the reported URL (see buildTrackingPath). The same path twice in a row is sent
 * once, so React Strict Mode remounts and query changes that do not alter it (date ranges, filters)
 * add nothing. ContentsquareInit calls this for the app's pages; the onboarding pages call it
 * themselves, once the visitor may be recorded.
 */
export function trackPage(trackingPath: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isTrackingEnabled() || tagLoadFailed) return;

  ensureQueue();

  const tagAlreadyPresent =
    hasBootstrappedTag ||
    typeof window.CS_CONF !== "undefined" ||
    Boolean(document.getElementById("contentsquare"));

  if (!tagAlreadyPresent) {
    pushCommand("setPath", trackingPath);

    const script = document.createElement("script");
    script.id = "contentsquare";
    script.type = "text/javascript";
    script.async = true;
    script.src = CONTENTSQUARE_SRC;
    script.onerror = () => {
      // Nothing will ever drain `_uxa` — stop tracking instead of growing the queue.
      tagLoadFailed = true;
      hasBootstrappedTag = false;
      script.remove();
    };
    document.head.appendChild(script);

    hasBootstrappedTag = true;
    lastTrackedPath = trackingPath;
    return;
  }

  hasBootstrappedTag = true;
  if (lastTrackedPath === trackingPath) return;

  lastTrackedPath = trackingPath;
  pushCommand("trackPageview", trackingPath);
}

/** Custom-var indexes (1-20) for Contentsquare identify attributes. */
const CUSTOM_VAR = {
  ROLE: 1,
  USER_NAME: 2,
  EMAIL: 3,
  COMPANY_ID: 4,
  COMPANY_NAME: 5,
} as const;

/** Contentsquare custom-variable scope: 2 = visit (persists for the session). */
const VISIT_SCOPE = 2;

/** The tag batches queued commands - give it a moment before a hard navigation. */
const FLUSH_WINDOW_MS = 300;

let isInitialized = false;
let identifiedUserId: string | null = null;
let lastIdentitySignature: string | null = null;
let lastCompanySelectedId: string | null = null;
const firedEvents = new Set<string>();

export function ensureQueue(): void {
  if (typeof window === "undefined") return;
  window._uxa = window._uxa || [];
}

/** Push a command onto the Contentsquare queue; safe before the tag has loaded. */
export function pushCommand(...args: unknown[]): void {
  ensureQueue();
  window._uxa?.push(args);
}

function getDedupeKey(eventName: ContentsquareEventName, dedupeKey?: string): string {
  return dedupeKey ? `${eventName}:${dedupeKey}` : eventName;
}

function hasFired(key: string): boolean {
  if (firedEvents.has(key)) return true;
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(`${DEDUPE_STORAGE_PREFIX}${key}`) === "1";
  } catch {
    return false;
  }
}

function markFired(key: string): void {
  firedEvents.add(key);
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(`${DEDUPE_STORAGE_PREFIX}${key}`, "1");
  } catch {
    // sessionStorage may be unavailable; in-memory set still prevents remount dupes
  }
}

function clearDedupeState(): void {
  firedEvents.clear();
  lastCompanySelectedId = null;
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(DEDUPE_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function toContentsquareRole(role: string): ContentsquareRoleLabel | string {
  switch (role) {
    case userRoles.SUPER_ADMIN:
      return "Super Admin";
    case userRoles.CLIENT_ADMIN:
      return "Client Admin";
    case userRoles.CSR_USER:
    case userRoles.SUPER_CSR_ADMIN:
      return "CSR";
    default:
      return role;
  }
}

/**
 * Ready when the Contentsquare Tag ID is configured.
 * Script injection is handled by ContentsquareInit.
 */
function init(): boolean {
  if (typeof window === "undefined") return false;
  if (!isTrackingEnabled()) return false;
  if (isInitialized) return true;

  ensureQueue();
  isInitialized = true;
  return true;
}

function setVisitCustomVar(index: number, name: string, value: string): void {
  pushCommand("setCustomVariable", index, name, value, VISIT_SCOPE);
}

function identifyUser(userId: string, attributes: ContentsquareIdentifyAttributes): void {
  if (!userId) return;
  if (!init()) return;

  const signature = JSON.stringify([
    userId,
    attributes.role,
    attributes.userName ?? "",
    attributes.email ?? "",
    attributes.companyId ?? "",
    attributes.companyName ?? "",
  ]);
  // The provider effect re-runs on company switches and SWR revalidations; without this
  // every re-run would re-send the identity and inflate the @user-identifier@ page event.
  if (lastIdentitySignature === signature) return;

  if (identifiedUserId !== userId) {
    // Session Replay filter key (works with the standard Contentsquare tag)
    pushCommand("trackPageEvent", `@user-identifier@${userId}`);
    // Product Analytics identity (queued; no-op if PA/Heap is not enabled)
    pushCommand("identify", userId);
    identifiedUserId = userId;
  }

  pushCommand("addUserProperties", {
    role: attributes.role,
    ...(attributes.userName ? { userName: attributes.userName } : {}),
    ...(attributes.email ? { email: attributes.email } : {}),
    ...(attributes.companyId ? { companyId: attributes.companyId } : {}),
    ...(attributes.companyName ? { companyName: attributes.companyName } : {}),
  });

  setVisitCustomVar(CUSTOM_VAR.ROLE, "role", String(attributes.role));
  if (attributes.userName) {
    setVisitCustomVar(CUSTOM_VAR.USER_NAME, "userName", attributes.userName);
  }
  if (attributes.email) {
    setVisitCustomVar(CUSTOM_VAR.EMAIL, "email", attributes.email);
  }
  if (attributes.companyId) {
    setVisitCustomVar(CUSTOM_VAR.COMPANY_ID, "companyId", attributes.companyId);
  }
  if (attributes.companyName) {
    setVisitCustomVar(CUSTOM_VAR.COMPANY_NAME, "companyName", attributes.companyName);
  }

  lastIdentitySignature = signature;
}

/** `companyName` is optional: a page that only knows the ID (the emailed setup guide) leaves the name as it is. */
function setCompanyContext(companyId: string, companyName?: string): void {
  if (!init()) return;
  setVisitCustomVar(CUSTOM_VAR.COMPANY_ID, "companyId", companyId);
  if (companyName) setVisitCustomVar(CUSTOM_VAR.COMPANY_NAME, "companyName", companyName);
}

/** Extra user properties (Product Analytics), e.g. the onboarding funnel and plan. Empty values are left out. */
function setUserProperties(properties: Record<string, string | null | undefined>): void {
  if (!init()) return;
  const set = Object.fromEntries(Object.entries(properties).filter(([, v]) => v));
  if (Object.keys(set).length === 0) return;
  pushCommand("addUserProperties", set);
}

function trackPageEvent(name: ContentsquareEventName): void {
  pushCommand("trackPageEvent", name);
}

function event(name: ContentsquareEventName, options?: { dedupeKey?: string }): void {
  if (!init()) return;

  if (name === ContentsquareEvent.COMPANY_SELECTED) {
    const companyId = options?.dedupeKey;
    if (!companyId || companyId === lastCompanySelectedId) return;
    lastCompanySelectedId = companyId;
    trackPageEvent(name);
    return;
  }

  // When a dedupeKey is provided, fire at most once per key per tab session.
  // Without a key, fire every call - callers guard transitions.
  if (options?.dedupeKey) {
    const key = getDedupeKey(name, options.dedupeKey);
    if (hasFired(key)) return;
    trackPageEvent(name);
    markFired(key);
    return;
  }

  trackPageEvent(name);
}

function reset(): void {
  identifiedUserId = null;
  lastIdentitySignature = null;
  clearDedupeState();

  if (typeof window === "undefined") return;
  if (!isInitialized) return;

  // Clear identity for the current Contentsquare session before navigating to login.
  pushCommand("resetIdentity");
}

/**
 * Resolves once the tag has had a chance to send what is queued. Await before a hard
 * navigation (logout), otherwise the last events are dropped with the document.
 */
function flush(): Promise<void> {
  if (typeof window === "undefined" || !isInitialized) return Promise.resolve();
  return new Promise(resolve => {
    window.setTimeout(resolve, FLUSH_WINDOW_MS);
  });
}

/** Fire a settings-tab view once per company + tab per session. */
export function trackSettingsTabViewed(companyId: string, tabId: string): void {
  if (!companyId || !tabId) return;
  const tabEvent = SETTINGS_TAB_EVENTS[tabId];
  if (!tabEvent) return;
  event(tabEvent, { dedupeKey: `${companyId}:${tabId}` });
}

export const contentsquare = {
  init,
  identifyUser,
  setCompanyContext,
  setUserProperties,
  trackPage,
  event,
  reset,
  flush,
  trackSettingsTabViewed,
  isReady: () => isInitialized,
  isUserIdentified: () => identifiedUserId !== null,
};

export default contentsquare;
